import {
	isSameDay,
	parseISO,
	startOfDay,
	startOfWeek,
	startOfMonth,
	startOfQuarter,
	startOfYear,
	subDays,
	subWeeks,
	subMonths,
	subQuarters,
	subYears
} from 'date-fns'
import { ArrowDown, ArrowUp, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { type ComponentType, type ReactNode, useId, useMemo, useState } from 'react'
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { z } from 'zod'

import { useListSalesChannels, usePaginatedListProducts } from '@/api/react-query'
import { Badge } from '@/components/base/badge'
import { Button } from '@/components/base/button'
import { Calendar } from '@/components/base/calendar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/base/card'
import { ChartContainer, ChartTooltip, ChartTooltipContent, CHART_SERIES_PRIMARY_STROKE } from '@/components/base/chart'
import { Checkbox } from '@/components/base/checkbox'
import { Combobox } from '@/components/base/combobox'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/base/popover'
import { Select } from '@/components/base/select'
import { Skeleton } from '@/components/base/skeleton'
import { Text } from '@/components/base/text'
import { SalesChannelMultiSelect, type SalesChannelOption } from '@/components/sales-channel-multi-select'
import { useI18n } from '@/i18n/i18n-react'
import { Check } from '@/icons/check'
import { TrianglesMini } from '@/icons/triangles-mini'
import { clx } from '@/utils/clx'
import { useDebounce } from '@/utils/hooks'

export const WEEK_STARTS_ON: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 1

export type AnalyticsInterval = 'day' | 'week' | 'month' | 'quarter' | 'year'

// The period pager mirrors the server's UTC calendar math (analytics-interval.ts) so the label and
// the Prev/Next targets line up with the periods the API actually returns, regardless of local tz.

/** UTC start of the calendar period (of the given interval) that contains `date`. */
export function startOfPeriodUtc(interval: AnalyticsInterval, date: Date): Date {
	const y = date.getUTCFullYear()
	const m = date.getUTCMonth()
	const d = date.getUTCDate()
	switch (interval) {
		case 'day':
			return new Date(Date.UTC(y, m, d))
		case 'week': {
			const dow = new Date(Date.UTC(y, m, d)).getUTCDay()
			const offset = (dow - WEEK_STARTS_ON + 7) % 7
			return new Date(Date.UTC(y, m, d - offset))
		}
		case 'month':
			return new Date(Date.UTC(y, m, 1))
		case 'quarter':
			return new Date(Date.UTC(y, Math.floor(m / 3) * 3, 1))
		case 'year':
			return new Date(Date.UTC(y, 0, 1))
	}
}

/** Shift a period start forward/backward by `delta` whole periods (UTC, calendar-aware). */
export function shiftPeriodUtc(interval: AnalyticsInterval, start: Date, delta: number): Date {
	const y = start.getUTCFullYear()
	const m = start.getUTCMonth()
	const d = start.getUTCDate()
	switch (interval) {
		case 'day':
			return new Date(Date.UTC(y, m, d + delta))
		case 'week':
			return new Date(Date.UTC(y, m, d + delta * 7))
		case 'month':
			return new Date(Date.UTC(y, m + delta, 1))
		case 'quarter':
			return new Date(Date.UTC(y, m + delta * 3, 1))
		case 'year':
			return new Date(Date.UTC(y + delta, 0, 1))
	}
}

/** The same period one year earlier, re-aligned to the interval's start (e.g. a Monday for weeks). */
export function samePeriodLastYearUtc(interval: AnalyticsInterval, start: Date): Date {
	const candidate = new Date(Date.UTC(start.getUTCFullYear() - 1, start.getUTCMonth(), start.getUTCDate()))
	return startOfPeriodUtc(interval, candidate)
}

/** `yyyy-MM-dd` param for a period start. */
export function periodParam(date: Date): string {
	return date.toISOString().slice(0, 10)
}

/** Parse a `periodStart` search param to the aligned period start, defaulting to the current period. */
export function selectedPeriodStart(interval: AnalyticsInterval, periodStart: string | undefined): Date {
	const base = periodStart ? new Date(`${periodStart}T00:00:00.000Z`) : new Date()
	return startOfPeriodUtc(interval, Number.isNaN(base.getTime()) ? new Date() : base)
}

/**
 * Sentinel `comparePeriod` value meaning "same period last year". Stored as a mode token rather than
 * a concrete date so it stays distinguishable from a specific period that happens to coincide with
 * last year's date, and so paging re-anchors it relative to the newly selected period.
 */
export const COMPARE_LAST_YEAR = 'lastYear'

/**
 * Resolve a `comparePeriod` search value to the concrete `yyyy-MM-dd` the API expects: the
 * {@link COMPARE_LAST_YEAR} sentinel becomes the same-period-last-year date for the selected period,
 * while `undefined`, `'none'`, and specific dates pass through unchanged.
 */
export function resolveComparePeriodParam(
	interval: AnalyticsInterval,
	periodStart: string | undefined,
	comparePeriod: string | undefined
): string | undefined {
	if (comparePeriod === COMPARE_LAST_YEAR) {
		return periodParam(samePeriodLastYearUtc(interval, selectedPeriodStart(interval, periodStart)))
	}
	return comparePeriod
}

/**
 * "vs …" label for the KPI cards, built from the server-provided comparison range (epoch-ms,
 * UTC, completed-bucket aligned) so the label always matches the span the overview actually
 * compared. Returns an empty string until the range is available.
 */
export function useComparisonLabel(range: { from: number; to: number } | undefined): string {
	const { L } = useI18n()
	if (!range) return ''
	return isSameDay(range.from, range.to)
		? L.Analytics.VsDate(L.Date.Short(range.from))
		: L.Analytics.VsDateRange(L.Date.Short(range.from), L.Date.Short(range.to))
}

export function getPreviousPeriodRange(interval: 'day' | 'week' | 'month' | 'quarter' | 'year'): {
	from: Date
	to: Date
} {
	const now = new Date()
	let currentStart: Date
	let previousStart: Date

	switch (interval) {
		case 'day':
			currentStart = startOfDay(now)
			previousStart = subDays(currentStart, 1)
			break
		case 'week':
			currentStart = startOfWeek(now, { weekStartsOn: WEEK_STARTS_ON })
			previousStart = subWeeks(currentStart, 1)
			break
		case 'month':
			currentStart = startOfMonth(now)
			previousStart = subMonths(currentStart, 1)
			break
		case 'quarter':
			currentStart = startOfQuarter(now)
			previousStart = subQuarters(currentStart, 1)
			break
		case 'year':
			currentStart = startOfYear(now)
			previousStart = subYears(currentStart, 1)
			break
	}

	const elapsedMs = now.getTime() - currentStart.getTime()
	const elapsedTo = new Date(previousStart.getTime() + elapsedMs)
	let previousTo = elapsedTo
	if (elapsedTo.getTime() >= currentStart.getTime()) {
		previousTo = new Date(currentStart.getTime() - 1)
	}
	return {
		from: previousStart,
		to: previousTo
	}
}

export function DeltaBadge({
	current,
	previous,
	invertPolarity = false
}: {
	current: number
	previous: number
	/** For metrics where growth is bad (deletion requests), so a rise reads as negative. */
	invertPolarity?: boolean
}) {
	const { L } = useI18n()

	if (current === 0 && previous === 0) {
		return (
			<Badge color='grey' className='text-xs'>
				<Text>0%</Text>
			</Badge>
		)
	}

	if (previous === 0 && current > 0) {
		return (
			<Badge color={invertPolarity ? 'red' : 'green'} className='text-xs'>
				<ArrowUp className='mr-1 h-3 w-3' />
				<Text>{L.Analytics.DeltaNew()}</Text>
			</Badge>
		)
	}

	if (previous === 0) return null

	const percentChange = ((current - previous) / previous) * 100

	if (Math.abs(percentChange) < 0.1) {
		return (
			<Badge color='grey' className='text-xs'>
				<Text>0%</Text>
			</Badge>
		)
	}

	// The threshold above rules out a neutral delta, so this is strictly up or down.
	const isIncrease = percentChange > 0
	const isFavourable = invertPolarity ? !isIncrease : isIncrease

	return (
		<Badge color={isFavourable ? 'green' : 'red'} className='text-xs'>
			{isIncrease ? <ArrowUp className='mr-1 h-3 w-3' /> : <ArrowDown className='mr-1 h-3 w-3' />}
			<Text>{Math.abs(percentChange).toFixed(1)}%</Text>
		</Badge>
	)
}

export function KPICard({
	title,
	current,
	previous,
	icon: Icon,
	formatter,
	comparisonLabel,
	invertPolarity = false,
	hasComparison = true
}: {
	title: string
	current: number
	previous: number
	icon: ComponentType<{ className?: string }>
	formatter?: (value: number) => string
	comparisonLabel: string
	invertPolarity?: boolean
	/** When false (comparison turned off), the delta badge + "vs" label are hidden. */
	hasComparison?: boolean
}) {
	const badge = hasComparison ? (
		<DeltaBadge current={current} previous={previous} invertPolarity={invertPolarity} />
	) : null
	const showComparison = badge !== null

	return (
		<Card>
			<CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
				<CardTitle className='text-sm font-medium'>{title}</CardTitle>
				<Icon className='text-muted-foreground h-4 w-4' />
			</CardHeader>
			<CardContent>
				<div className='text-2xl font-bold'>{formatter ? formatter(current) : current.toLocaleString()}</div>
				{showComparison && (
					<div className='mt-2 flex items-center gap-2'>
						{badge}
						<Text className='text-muted-foreground text-xs'>{comparisonLabel}</Text>
					</div>
				)}
			</CardContent>
		</Card>
	)
}

const ALL_INTERVALS: AnalyticsInterval[] = ['day', 'week', 'month', 'quarter', 'year']

/**
 * Shared width for the analytics toolbar controls (period picker, channel filter, comparison
 * selectors): full width when stacked on small screens, a fixed column from `sm` up so the controls
 * line up into even, aligned columns that wrap together.
 */
export const CONTROL_WIDTH = 'w-full shrink-0 sm:w-[13rem]'

/** The five interval labels for the current locale. */
export function useIntervalLabels(): Record<AnalyticsInterval, string> {
	const { L } = useI18n()
	return {
		day: L.Analytics.Day(),
		week: L.Analytics.Week(),
		month: L.Analytics.Month(),
		quarter: L.Analytics.Quarter(),
		year: L.Analytics.Year()
	}
}

/** Renders the human label for the selected period (e.g. `8 – 14 Jun`, `June 2026`, `Q2 2026`). */
export function usePeriodLabel(interval: AnalyticsInterval, start: Date): string {
	const { L } = useI18n()
	switch (interval) {
		case 'day':
			return L.Date.Short(start.getTime())
		case 'week': {
			const end = new Date(shiftPeriodUtc('week', start, 1).getTime() - 1)
			return `${L.Date.Short(start.getTime())} – ${L.Date.Short(end.getTime())}`
		}
		case 'month': {
			// Compose from the lowercase month formatter (not MonthAndYear, which force-capitalises) so the
			// casing follows the active locale's orthography and matches the dropdown options — e.g. Icelandic
			// "október 2026" rather than "Október 2026". The supported locales all order this "month year".
			const local = new Date(start.getUTCFullYear(), start.getUTCMonth(), 1)
			return `${L.Date.Month(local.getTime())} ${local.getFullYear()}`
		}
		case 'quarter':
			return L.Analytics.QuarterLabel({
				quarter: Math.floor(start.getUTCMonth() / 3) + 1,
				year: start.getUTCFullYear()
			})
		case 'year':
			return String(start.getUTCFullYear())
	}
}

/** `Date.UTC` midnight for the y/m/d a calendar (local-time) Date represents, so picker clicks map to UTC periods. */
function calendarDayToUtc(date: Date): Date {
	return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
}

/** Inverse of {@link calendarDayToUtc}: a local-midnight Date for a UTC period start, so the calendar highlights the right cell. */
function utcToCalendarDay(date: Date): Date {
	return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

/** Per-interval "vs a specific …" prefix for the specific-period comparison option. */
function useSpecificPrefix(interval: AnalyticsInterval): string {
	const { L } = useI18n()
	switch (interval) {
		case 'day':
			return L.Analytics.CompareSpecific.Day()
		case 'week':
			return L.Analytics.CompareSpecific.Week()
		case 'month':
			return L.Analytics.CompareSpecific.Month()
		case 'quarter':
			return L.Analytics.CompareSpecific.Quarter()
		case 'year':
			return L.Analytics.CompareSpecific.Year()
	}
}

/**
 * Period comparison: previous period (default), same period last year, a specific earlier period, or
 * none. The trigger and menu items spell out the exact calendar period being compared against (e.g.
 * `vs May 2026`) using the same UTC period math as the pager, so it's always clear what each option
 * means. Picking "a specific …" swaps the same popover to an interval-aware picker.
 */
export function CompareSelect({
	interval,
	periodStart,
	comparePeriod,
	earliestSelectable,
	onChange,
	channelCompare,
	className
}: {
	interval: AnalyticsInterval
	periodStart: string | undefined
	comparePeriod: string | undefined
	/** Start (UTC) of the first sale; comparison periods before it are disabled. Omit for no bound. */
	earliestSelectable?: Date
	/** Width/layout overrides for the trigger so it can line up with the sibling controls. */
	className?: string
	/** `undefined` = previous period, `'none'` = off, else a `yyyy-MM-dd` comparison period start. */
	onChange: (comparePeriod: string | undefined) => void
	/**
	 * When provided, the menu gains an "other sales channels" comparison that swaps the popover to a
	 * channel checklist. A non-empty `value` means channel-comparison mode. Choosing a period option
	 * clears the channels (and vice-versa) via the callers' patch handlers, since the two are
	 * mutually exclusive on the API.
	 */
	channelCompare?: {
		salesChannels: SalesChannelOption[]
		value: string[]
		onChange: (uuids: string[]) => void
		isLoading?: boolean
	}
}) {
	const { L } = useI18n()
	// A single popover drives the option menu, the period picker and the channel checklist. Using one
	// Radix layer avoids the focus/dismiss races of nesting another popover inside a Select.
	const [open, setOpen] = useState(false)
	const [panel, setPanel] = useState<'menu' | 'period' | 'channels'>('menu')
	const selected = selectedPeriodStart(interval, periodStart)
	const previousLabel = usePeriodLabel(interval, shiftPeriodUtc(interval, selected, -1))
	const lastYearStart = samePeriodLastYearUtc(interval, selected)
	const lastYearLabel = usePeriodLabel(interval, lastYearStart)

	const channelValue = channelCompare?.value ?? []
	const channelMode = channelValue.length > 0
	const channelLabel =
		channelValue.length === 1
			? (channelCompare?.salesChannels.find(c => c.uuid === channelValue[0])?.name ??
				L.Analytics.BasketAnalysis.SalesChannelsSelected({ count: 1 }))
			: L.Analytics.BasketAnalysis.SalesChannelsSelected({ count: channelValue.length })

	// Anything that isn't a mode token (`none`/`lastYear`) and parses to a valid date is a specific period.
	const parsedCompare =
		comparePeriod && comparePeriod !== 'none' && comparePeriod !== COMPARE_LAST_YEAR
			? new Date(`${comparePeriod}T00:00:00.000Z`)
			: undefined
	const validCompare = parsedCompare && !Number.isNaN(parsedCompare.getTime()) ? parsedCompare : undefined
	const specificStart = validCompare ? startOfPeriodUtc(interval, validCompare) : undefined
	// Always call the label hook; the fallback value is unused unless `mode === 'specific'`.
	const specificLabel = usePeriodLabel(interval, specificStart ?? shiftPeriodUtc(interval, selected, -1))

	let mode = 'previous'
	if (channelMode) mode = 'channel'
	else if (comparePeriod === 'none') mode = 'none'
	else if (comparePeriod === COMPARE_LAST_YEAR) mode = 'year'
	else if (specificStart) mode = 'specific'

	// The trigger stays compact (`vs May 2026`) while the menu items carry the fuller wording.
	let triggerLabel = L.Analytics.VsDate(previousLabel)
	if (mode === 'channel') triggerLabel = L.Analytics.VsDate(channelLabel)
	else if (mode === 'none') triggerLabel = L.Analytics.CompareNone()
	else if (mode === 'year') triggerLabel = L.Analytics.VsDate(lastYearLabel)
	else if (mode === 'specific') triggerLabel = L.Analytics.VsDate(specificLabel)

	const specificPrefix = useSpecificPrefix(interval)
	const specificItemLabel = mode === 'specific' ? `${specificPrefix} (${specificLabel})` : specificPrefix

	const choose = (next: string | undefined) => {
		onChange(next)
		setPanel('menu')
		setOpen(false)
	}
	const toggleChannel = (uuid: string) => {
		if (!channelCompare) return
		channelCompare.onChange(
			channelValue.includes(uuid) ? channelValue.filter(v => v !== uuid) : [...channelValue, uuid]
		)
	}

	return (
		<Popover
			open={open}
			onOpenChange={next => {
				setOpen(next)
				// Always land on the option menu when (re)opening; sub-panels appear only after a choice.
				setPanel('menu')
			}}
		>
			<PopoverTrigger
				className={clx(
					'bg-ui-bg-field shadow-buttons-neutral transition-fg flex h-8 w-[13rem] select-none items-center justify-between gap-2 rounded-md px-2 py-1.5 outline-none',
					'txt-compact-small text-ui-fg-base',
					'hover:bg-ui-bg-field-hover',
					'focus-visible:shadow-borders-interactive-with-active data-[state=open]:shadow-borders-interactive-with-active',
					className
				)}
			>
				<span className='truncate'>{triggerLabel}</span>
				<TrianglesMini className='shrink-0 text-ui-fg-muted' />
			</PopoverTrigger>
			<PopoverContent align='start' className='w-auto p-0'>
				{panel === 'period' && (
					<PeriodPicker
						interval={interval}
						latestSelectable={shiftPeriodUtc(interval, selected, -1)}
						earliestSelectable={earliestSelectable}
						value={specificStart}
						onPick={date => choose(periodParam(date))}
					/>
				)}
				{panel === 'channels' && channelCompare && (
					<div className='flex max-h-[20rem] w-[14rem] flex-col'>
						<div className='max-h-[16rem] overflow-y-auto p-1'>
							{channelCompare.isLoading ? (
								<div className='space-y-1 p-1'>
									{[...Array(4)].map((_, i) => (
										<Skeleton key={i} className='h-7 w-full' />
									))}
								</div>
							) : (
								channelCompare.salesChannels.map(channel => (
									<button
										key={channel.uuid}
										type='button'
										onClick={() => toggleChannel(channel.uuid)}
										className='txt-compact-small flex w-full items-center gap-x-2 rounded-[4px] px-2 py-1.5 text-left outline-none transition-colors hover:bg-ui-bg-base-hover focus-visible:bg-ui-bg-base-hover'
									>
										<Checkbox checked={channelValue.includes(channel.uuid)} />
										<span className='truncate'>{channel.name}</span>
									</button>
								))
							)}
						</div>
						<div className='border-t border-ui-border-base p-2'>
							<Button
								size='small'
								variant='secondary'
								className='w-full'
								onClick={() => setPanel('menu')}
							>
								{L.General.Close()}
							</Button>
						</div>
					</div>
				)}
				{panel === 'menu' && (
					<div className='min-w-[13rem] p-1'>
						<CompareMenuItem selected={mode === 'previous'} onSelect={() => choose(undefined)}>
							{`${L.Analytics.ComparePreviousPeriod()} (${previousLabel})`}
						</CompareMenuItem>
						<CompareMenuItem selected={mode === 'year'} onSelect={() => choose(COMPARE_LAST_YEAR)}>
							{`${L.Analytics.CompareSamePeriodLastYear()} (${lastYearLabel})`}
						</CompareMenuItem>
						<CompareMenuItem selected={mode === 'specific'} onSelect={() => setPanel('period')}>
							{specificItemLabel}
						</CompareMenuItem>
						{channelCompare && (
							<CompareMenuItem selected={mode === 'channel'} onSelect={() => setPanel('channels')}>
								{mode === 'channel'
									? `${L.Analytics.CompareToChannel()} (${channelLabel})`
									: L.Analytics.CompareToChannel()}
							</CompareMenuItem>
						)}
						<CompareMenuItem selected={mode === 'none'} onSelect={() => choose('none')}>
							{L.Analytics.CompareNone()}
						</CompareMenuItem>
					</div>
				)}
			</PopoverContent>
		</Popover>
	)
}

/** A single option row in the compare menu, mirroring the look of the base `Select.Item`. */
function CompareMenuItem({
	selected,
	onSelect,
	children
}: {
	selected: boolean
	onSelect: () => void
	children: ReactNode
}) {
	return (
		<button
			type='button'
			onClick={onSelect}
			className={clx(
				'txt-compact-small grid w-full grid-cols-[15px_1fr] items-center gap-x-2 rounded-[4px] px-2 py-1.5 text-left outline-none transition-colors',
				'hover:bg-ui-bg-base-hover focus-visible:bg-ui-bg-base-hover',
				selected && 'txt-compact-small-plus'
			)}
		>
			<span className='flex h-[15px] w-[15px] items-center justify-center'>{selected && <Check />}</span>
			<span className='truncate'>{children}</span>
		</button>
	)
}

/** Header row for the grid pickers: a centered label flanked by prev/next year (or page) chevrons. */
function PickerNav({
	label,
	onPrev,
	onNext,
	canGoNext,
	canGoPrev = true
}: {
	label: string
	onPrev: () => void
	onNext: () => void
	canGoNext: boolean
	/** Defaults to true; set false to stop paging before the earliest period with data. */
	canGoPrev?: boolean
}) {
	const { L } = useI18n()
	return (
		<div className='flex items-center justify-between'>
			<Button
				size='base'
				variant='secondary'
				className='h-7 w-7 px-0'
				disabled={!canGoPrev}
				onClick={onPrev}
				aria-label={L.Pagination.Previous()}
			>
				<ChevronLeft className='h-4 w-4' />
			</Button>
			<Text size='small' weight='plus' className='tabular-nums'>
				{label}
			</Text>
			<Button
				size='base'
				variant='secondary'
				className='h-7 w-7 px-0'
				disabled={!canGoNext}
				onClick={onNext}
				aria-label={L.Pagination.Next()}
			>
				<ChevronRight className='h-4 w-4' />
			</Button>
		</div>
	)
}

/**
 * Period picker matching the active interval. Day/week use the calendar; month/quarter/year use
 * compact grids with year (or page) navigation. Periods starting after `latestSelectable` are
 * disabled: the comparison picker passes the period before the selected one (comparisons are always
 * earlier), the jump picker passes the current period (no future periods).
 */
function PeriodPicker({
	interval,
	latestSelectable,
	earliestSelectable,
	value,
	onPick
}: {
	interval: AnalyticsInterval
	/** Start (UTC) of the latest period that can be picked, inclusive. */
	latestSelectable: Date
	/** Start (UTC) of the first sale; periods/years before it are disabled. Omit for no lower bound. */
	earliestSelectable?: Date
	/** Currently chosen period start (UTC), if any. */
	value: Date | undefined
	onPick: (periodStart: Date) => void
}) {
	if (interval === 'day' || interval === 'week') {
		return (
			<CalendarPeriodPicker
				interval={interval}
				latestSelectable={latestSelectable}
				earliestSelectable={earliestSelectable}
				value={value}
				onPick={onPick}
			/>
		)
	}
	if (interval === 'month') {
		return (
			<MonthPeriodPicker
				latestSelectable={latestSelectable}
				earliestSelectable={earliestSelectable}
				value={value}
				onPick={onPick}
			/>
		)
	}
	if (interval === 'quarter') {
		return (
			<QuarterPeriodPicker
				latestSelectable={latestSelectable}
				earliestSelectable={earliestSelectable}
				value={value}
				onPick={onPick}
			/>
		)
	}
	return (
		<YearPeriodPicker
			latestSelectable={latestSelectable}
			earliestSelectable={earliestSelectable}
			value={value}
			onPick={onPick}
		/>
	)
}

/** The whole UTC period (a single day, or a 7-day week span) that a calendar day belongs to, in calendar-local dates. */
function periodRangeForDay(interval: 'day' | 'week', day: Date): { from: Date; to: Date } {
	const start = startOfPeriodUtc(interval, calendarDayToUtc(day))
	return {
		from: utcToCalendarDay(start),
		to: utcToCalendarDay(interval === 'week' ? shiftPeriodUtc('day', start, 6) : start)
	}
}

/**
 * Day/week picker built on the shared calendar. Week clicks snap to the UTC week start. The calendar's
 * built-in nav is hidden in favour of the shared {@link PickerNav} header (so day/week match the
 * month/quarter/year grids), and hovering highlights the exact day or week that a click would select.
 */
function CalendarPeriodPicker({
	interval,
	latestSelectable,
	earliestSelectable,
	value,
	onPick
}: {
	interval: 'day' | 'week'
	latestSelectable: Date
	earliestSelectable?: Date
	value: Date | undefined
	onPick: (periodStart: Date) => void
}) {
	const { L } = useI18n()
	const latestLocal = utcToCalendarDay(latestSelectable)
	const earliestLocal = earliestSelectable ? utcToCalendarDay(earliestSelectable) : undefined
	const seed = utcToCalendarDay(value ?? latestSelectable)
	const [month, setMonth] = useState(new Date(seed.getFullYear(), seed.getMonth(), 1))
	const [hovered, setHovered] = useState<Date | null>(null)

	// A custom modifier (not the built-in `selected`, whose shadcn token is unset in this theme) marks
	// the chosen day/week; `hovered` previews the day/week the cursor is over.
	const chosen = value !== undefined ? periodRangeForDay(interval, utcToCalendarDay(value)) : undefined
	const hoveredRange = hovered ? periodRangeForDay(interval, hovered) : undefined

	const canGoNext =
		month.getFullYear() < latestLocal.getFullYear() ||
		(month.getFullYear() === latestLocal.getFullYear() && month.getMonth() < latestLocal.getMonth())
	// Stop paging once the displayed month reaches the month of the first sale.
	const canGoPrev =
		!earliestLocal ||
		month.getFullYear() > earliestLocal.getFullYear() ||
		(month.getFullYear() === earliestLocal.getFullYear() && month.getMonth() > earliestLocal.getMonth())

	// A day is out of range when its period ends after the latest selectable period, or when it starts
	// before the first sale's period.
	const isOutOfRange = (date: Date) => {
		const periodStart = startOfPeriodUtc(interval, calendarDayToUtc(date)).getTime()
		if (periodStart > latestSelectable.getTime()) return true
		if (earliestSelectable && periodStart < startOfPeriodUtc(interval, earliestSelectable).getTime()) return true
		return false
	}

	return (
		<div className='w-full p-3'>
			{/* The day grid has fixed-width cells (7×2rem), narrower than the popover. Constrain the whole
			    calendar block to that width and centre it so the nav chevrons align with the grid edges and
			    the left/right padding stays symmetric. */}
			<div className='mx-auto w-fit space-y-3'>
				<PickerNav
					// Lowercase month formatter (see usePeriodLabel) so the casing matches the day cells and the
					// locale's orthography — Icelandic "október 2026", not "Október 2026".
					label={`${L.Date.Month(month.getTime())} ${month.getFullYear()}`}
					onPrev={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
					onNext={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
					canGoNext={canGoNext}
					canGoPrev={canGoPrev}
				/>
				<Calendar
					className='p-0'
					// The shared PickerNav above already shows the month + year, so hide the day-picker's own caption.
					classNames={{ month_caption: 'hidden' }}
					mode='single'
					weekStartsOn={WEEK_STARTS_ON}
					month={month}
					onMonthChange={setMonth}
					hideNavigation
					startMonth={earliestLocal}
					endMonth={latestLocal}
					modifiers={{ ...(chosen ? { chosen } : {}), ...(hoveredRange ? { hovered: hoveredRange } : {}) }}
					modifiersClassNames={{
						chosen: 'bg-ui-button-inverted text-ui-contrast-fg-primary rounded-md',
						hovered: 'bg-ui-bg-base-hover rounded-md'
					}}
					disabled={isOutOfRange}
					onDayMouseEnter={date => {
						if (isOutOfRange(date)) return
						setHovered(date)
					}}
					onDayMouseLeave={() => setHovered(null)}
					onSelect={date => {
						if (date) onPick(startOfPeriodUtc(interval, calendarDayToUtc(date)))
					}}
				/>
			</div>
		</div>
	)
}

/** Month grid: a 12-month grid paged by year. */
function MonthPeriodPicker({
	latestSelectable,
	earliestSelectable,
	value,
	onPick
}: {
	latestSelectable: Date
	earliestSelectable?: Date
	value: Date | undefined
	onPick: (periodStart: Date) => void
}) {
	const { L } = useI18n()
	const maxYear = latestSelectable.getUTCFullYear()
	const minYear = earliestSelectable?.getUTCFullYear()
	const earliestMonthStart = earliestSelectable ? startOfPeriodUtc('month', earliestSelectable).getTime() : undefined
	const [displayYear, setDisplayYear] = useState(value ? value.getUTCFullYear() : maxYear)

	return (
		<div className='w-full space-y-3 p-3'>
			<PickerNav
				label={String(displayYear)}
				onPrev={() => setDisplayYear(y => y - 1)}
				onNext={() => setDisplayYear(y => y + 1)}
				canGoNext={displayYear < maxYear}
				canGoPrev={minYear === undefined || displayYear > minYear}
			/>
			<div className='grid grid-cols-3 gap-2'>
				{Array.from({ length: 12 }, (_, m) => {
					const start = new Date(Date.UTC(displayYear, m, 1))
					// `L.Date.Month` formats in the browser's local zone, so feed it a local-midnight date;
					// `start` (UTC) still drives selection/paging to keep the stored period correct.
					const labelDate = new Date(displayYear, m, 1)
					const isSelected = value?.getUTCFullYear() === displayYear && value?.getUTCMonth() === m
					const isDisabled =
						start.getTime() > latestSelectable.getTime() ||
						(earliestMonthStart !== undefined && start.getTime() < earliestMonthStart)
					return (
						<Button
							key={m}
							size='base'
							className='h-9 w-full min-w-0 px-2'
							variant={isSelected ? 'primary' : 'secondary'}
							disabled={isDisabled}
							onClick={() => onPick(start)}
						>
							<Text size='small' className='truncate'>
								{L.Date.Month(labelDate.getTime())}
							</Text>
						</Button>
					)
				})}
			</div>
		</div>
	)
}

/** Quarter grid: Q1-Q4 paged by year. */
function QuarterPeriodPicker({
	latestSelectable,
	earliestSelectable,
	value,
	onPick
}: {
	latestSelectable: Date
	earliestSelectable?: Date
	value: Date | undefined
	onPick: (periodStart: Date) => void
}) {
	const { L } = useI18n()
	const maxYear = latestSelectable.getUTCFullYear()
	const minYear = earliestSelectable?.getUTCFullYear()
	const earliestQuarterStart = earliestSelectable
		? startOfPeriodUtc('quarter', earliestSelectable).getTime()
		: undefined
	const [displayYear, setDisplayYear] = useState(value ? value.getUTCFullYear() : maxYear)
	const selectedQuarter = value?.getUTCFullYear() === displayYear ? Math.floor(value.getUTCMonth() / 3) : undefined

	return (
		<div className='w-full space-y-3 p-3'>
			<PickerNav
				label={String(displayYear)}
				onPrev={() => setDisplayYear(y => y - 1)}
				onNext={() => setDisplayYear(y => y + 1)}
				canGoNext={displayYear < maxYear}
				canGoPrev={minYear === undefined || displayYear > minYear}
			/>
			<div className='grid grid-cols-2 gap-2'>
				{Array.from({ length: 4 }, (_, q) => {
					const start = new Date(Date.UTC(displayYear, q * 3, 1))
					const isDisabled =
						start.getTime() > latestSelectable.getTime() ||
						(earliestQuarterStart !== undefined && start.getTime() < earliestQuarterStart)
					return (
						<Button
							key={q}
							size='base'
							className='h-9 w-full min-w-0'
							variant={selectedQuarter === q ? 'primary' : 'secondary'}
							disabled={isDisabled}
							onClick={() => onPick(start)}
						>
							<Text size='small' className='truncate'>
								{L.Analytics.QuarterLabel({ quarter: q + 1, year: displayYear })}
							</Text>
						</Button>
					)
				})}
			</div>
		</div>
	)
}

/** Year grid: 12 years per page, newest page first. */
function YearPeriodPicker({
	latestSelectable,
	earliestSelectable,
	value,
	onPick
}: {
	latestSelectable: Date
	earliestSelectable?: Date
	value: Date | undefined
	onPick: (periodStart: Date) => void
}) {
	const maxYear = latestSelectable.getUTCFullYear()
	const minYear = earliestSelectable?.getUTCFullYear()
	const initialEnd = value ? Math.min(value.getUTCFullYear(), maxYear) : maxYear
	const [endYear, setEndYear] = useState(initialEnd)
	const startYear = endYear - 11

	return (
		<div className='w-full space-y-3 p-3'>
			<PickerNav
				label={`${startYear} – ${endYear}`}
				onPrev={() => setEndYear(y => y - 12)}
				onNext={() => setEndYear(y => Math.min(maxYear, y + 12))}
				canGoNext={endYear < maxYear}
				canGoPrev={minYear === undefined || startYear > minYear}
			/>
			<div className='grid grid-cols-3 gap-2'>
				{Array.from({ length: 12 }, (_, i) => {
					const year = startYear + i
					const start = new Date(Date.UTC(year, 0, 1))
					const isDisabled = year > maxYear || (minYear !== undefined && year < minYear)
					return (
						<Button
							key={year}
							size='base'
							className='h-9 w-full min-w-0'
							variant={value?.getUTCFullYear() === year ? 'primary' : 'secondary'}
							disabled={isDisabled}
							onClick={() => onPick(start)}
						>
							<Text size='small' className='tabular-nums'>
								{String(year)}
							</Text>
						</Button>
					)
				})}
			</div>
		</div>
	)
}

/**
 * The period label, rendered as a trigger that opens an interval-aware picker. The popover also hosts
 * the interval selector, so all date selection (granularity + period) lives in one dropdown. The
 * merchant can jump straight to any period up to the current one instead of paging back.
 */
export function PeriodJumpPicker({
	interval,
	periodStart,
	earliestSelectable,
	onChange,
	onIntervalChange,
	className
}: {
	interval: AnalyticsInterval
	periodStart: string | undefined
	/** Start (UTC) of the first sale; periods before it are disabled. Omit for no lower bound. */
	earliestSelectable?: Date
	/** Receives the new `periodStart` param, or `undefined` for the current period. */
	onChange: (periodStart: string | undefined) => void
	/** Receives a newly chosen interval (the caller re-anchors the period). */
	onIntervalChange: (interval: AnalyticsInterval) => void
	/** Width/layout overrides for the trigger so it can line up with the sibling controls. */
	className?: string
}) {
	const { L } = useI18n()
	const intervalLabels = useIntervalLabels()
	const [open, setOpen] = useState(false)
	const selected = selectedPeriodStart(interval, periodStart)
	const currentStart = startOfPeriodUtc(interval, new Date())
	const isCurrent = selected.getTime() >= currentStart.getTime()
	const label = usePeriodLabel(interval, selected)

	// One fixed popover width for every interval so the dropdown never resizes as the selection changes.
	// It is sized for the widest picker (the month-name grid, whose options use a smaller font so this
	// stays compact); narrower pickers (the day/week calendar) are centred within it with symmetric padding.
	const popoverWidth = 'w-[19rem]'

	const pick = (start: Date) => {
		onChange(start.getTime() >= currentStart.getTime() ? undefined : periodParam(start))
		setOpen(false)
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				aria-label={L.Analytics.JumpToPeriod()}
				className={clx(
					// Match the field styling of the other dropdowns (Select / compare / channel triggers):
					// a bordered field with the neutral button shadow, not a borderless hover target.
					'bg-ui-bg-field shadow-buttons-neutral transition-fg flex h-8 select-none items-center gap-1.5 rounded-md px-2 py-1.5 outline-none',
					'txt-compact-small-plus tabular-nums text-ui-fg-base',
					'hover:bg-ui-bg-field-hover',
					'focus-visible:shadow-borders-interactive-with-active data-[state=open]:!shadow-borders-interactive-with-active',
					className
				)}
			>
				<CalendarDays className='h-3.5 w-3.5 shrink-0' />
				<span className='truncate'>{label}</span>
				<TrianglesMini className='ml-auto shrink-0 text-ui-fg-muted' />
			</PopoverTrigger>
			<PopoverContent align='start' className={clx('overflow-hidden rounded-lg p-0', popoverWidth)}>
				{/* Interval selector: keeps granularity + period in one dropdown. Five equal columns; the
				    popover width is sized so each label stays on a single line (Icelandic abbreviates the
				    long "Ársfjórðungur" to "Ársfj."). */}
				<div className='grid grid-cols-5 gap-1 border-b border-ui-border-base p-2'>
					{ALL_INTERVALS.map(iv => (
						<Button
							key={iv}
							size='small'
							variant={iv === interval ? 'primary' : 'secondary'}
							className='h-7 w-full px-0.5'
							onClick={() => onIntervalChange(iv)}
						>
							<Text size='xsmall' leading='compact' className='w-full whitespace-nowrap text-center'>
								{intervalLabels[iv]}
							</Text>
						</Button>
					))}
				</div>
				<PeriodPicker
					// Remount per interval so the grids re-seed their displayed year/page.
					key={interval}
					interval={interval}
					latestSelectable={currentStart}
					earliestSelectable={earliestSelectable}
					value={selected}
					onPick={pick}
				/>
				<div className='border-t border-ui-border-base p-2'>
					<Button
						size='small'
						variant='secondary'
						className='w-full'
						disabled={isCurrent}
						onClick={() => pick(currentStart)}
					>
						{L.Analytics.CurrentPeriod()}
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	)
}

/**
 * Search params shared by the Sales view and the reports: the selected period, its comparison, and
 * the channel filter / comparison channels. Keeping one schema means "View report" links (and the
 * reverse) carry the merchant's selection over unchanged.
 */
export const analyticsPeriodSearchSchema = z.object({
	interval: z.enum(['day', 'week', 'month', 'quarter', 'year']).optional().default('month'),
	// UTC start (yyyy-MM-dd) of the selected period; omit for the current period.
	periodStart: z.string().optional(),
	// Comparison selection: omit = previous period, 'none' = disabled, 'lastYear' = same period last
	// year (re-anchored relative to the selected period), or a yyyy-MM-dd start for a specific period.
	comparePeriod: z.string().optional(),
	// Channel filter (union of individual channels; selecting a group checks its channels).
	channels: z.array(z.string()).optional(),
	// Comparison channel set (union; selecting a group checks its channels), mutually exclusive with comparePeriod.
	compareChannels: z.array(z.string()).optional()
})

export type AnalyticsPeriodSearch = z.output<typeof analyticsPeriodSearchSchema>

/** Pick only the shared period/channel params out of a route's search (for cross-screen links). */
export function pickPeriodSearch(search: AnalyticsPeriodSearch): AnalyticsPeriodSearch {
	return {
		interval: search.interval,
		periodStart: search.periodStart,
		comparePeriod: search.comparePeriod,
		channels: search.channels,
		compareChannels: search.compareChannels
	}
}

/** API query params for the sales-analytics endpoints, derived from the shared search params. */
export function analyticsApiQuery(search: AnalyticsPeriodSearch) {
	// Resolve the `lastYear` sentinel to a concrete date for the API; 'none'/specific/omitted pass through.
	const resolvedComparePeriod = resolveComparePeriodParam(search.interval, search.periodStart, search.comparePeriod)
	return {
		interval: search.interval,
		...(search.periodStart ? { periodStart: search.periodStart } : {}),
		...(resolvedComparePeriod ? { comparePeriod: resolvedComparePeriod } : {}),
		...(search.channels && search.channels.length > 0 ? { salesChannelUuids: search.channels } : {}),
		...(search.compareChannels && search.compareChannels.length > 0
			? { compareSalesChannelUuids: search.compareChannels }
			: {})
	}
}

/**
 * "vs …" label for a sales comparison. In channel-comparison mode it names the compared channel(s)
 * (their name for a single channel, else a count); otherwise it is the compared date range.
 */
export function useSalesComparisonLabel(
	compareChannels: string[] | undefined,
	comparisonRange: { from: number; to: number } | undefined
): string {
	const { L } = useI18n()
	const { data: salesChannels } = useListSalesChannels()
	const dateLabel = useComparisonLabel(comparisonRange)
	const selected = compareChannels ?? []
	if (selected.length === 0) return dateLabel
	if (selected.length === 1) {
		const name =
			salesChannels?.find(sc => sc.uuid === selected[0])?.name ??
			L.Analytics.BasketAnalysis.SalesChannelsSelected({ count: 1 })
		return L.Analytics.VsDate(name)
	}
	return L.Analytics.VsDate(L.Analytics.BasketAnalysis.SalesChannelsSelected({ count: selected.length }))
}

/** What the sales overview compares against: another period, another channel, or nothing. */
export type ComparisonMode = 'period' | 'channel' | 'none'

/**
 * The comparison picker shared by the Sales view and the report page: a "Compare by …" mode dropdown
 * (period / channel / none) with the matching control beside it — the period {@link CompareSelect} or
 * the comparison-channel picker. The parent owns the `mode` state and maps each change to its own
 * search params, since the two screens differ on whether comparison is on by default.
 */
export function ComparisonControls({
	interval,
	periodStart,
	earliestSelectable,
	mode,
	onModeChange,
	compareSelectValue,
	onCompareSelect,
	channels,
	onChannelsChange,
	salesChannels,
	channelsLoading
}: {
	interval: AnalyticsInterval
	periodStart: string | undefined
	/** Start (UTC) of the first sale; comparison periods before it are disabled. Omit for no bound. */
	earliestSelectable?: Date
	mode: ComparisonMode
	onModeChange: (mode: ComparisonMode) => void
	/** {@link CompareSelect} value for period mode (`undefined` = previous period). */
	compareSelectValue: string | undefined
	onCompareSelect: (comparePeriod: string | undefined) => void
	channels: string[]
	onChannelsChange: (uuids: string[]) => void
	salesChannels: SalesChannelOption[]
	channelsLoading?: boolean
}) {
	const { L } = useI18n()
	let modeLabel = L.Analytics.CompareByPeriod()
	if (mode === 'channel') modeLabel = L.Analytics.CompareByChannel()
	else if (mode === 'none') modeLabel = L.Analytics.CompareNone()

	// Returned as a fragment (no wrapper) so the mode dropdown and its matching control become direct
	// children of the caller's flex row, lining up and wrapping together with the sibling controls.
	return (
		<>
			<Select value={mode} onValueChange={value => onModeChange(value as ComparisonMode)}>
				<Select.Trigger className={CONTROL_WIDTH}>
					<span className='truncate'>{modeLabel}</span>
				</Select.Trigger>
				<Select.Content>
					<Select.Item value='period'>{L.Analytics.CompareByPeriod()}</Select.Item>
					<Select.Item value='channel'>{L.Analytics.CompareByChannel()}</Select.Item>
					<Select.Item value='none'>{L.Analytics.CompareNone()}</Select.Item>
				</Select.Content>
			</Select>
			{mode === 'period' && (
				<CompareSelect
					interval={interval}
					periodStart={periodStart}
					comparePeriod={compareSelectValue}
					earliestSelectable={earliestSelectable}
					onChange={onCompareSelect}
					className={CONTROL_WIDTH}
				/>
			)}
			{mode === 'channel' && (
				<SalesChannelMultiSelect
					salesChannels={salesChannels}
					value={channels}
					onChange={onChannelsChange}
					allLabel={L.Analytics.CompareToChannel()}
					isLoading={channelsLoading}
					className={CONTROL_WIDTH}
				/>
			)}
		</>
	)
}

/**
 * Header controls for the period-based analytics screens: a "Period: …" jump picker and an interval
 * dropdown on one row, then (by `variant`):
 * - `sales`: a "Compare by" dropdown (period / channel / none) with the matching comparison control;
 * - `period`: the period comparison dropdown only (Users view);
 * - `navigation`: no comparison; `filterSlot` sits beside the interval dropdown (Basket view).
 *
 * The controls stack vertically (`gap-3`) and wrap within each row, so they lay out cleanly from
 * mobile up to `lg` where the whole cluster sits inline.
 */
export function AnalyticsPeriodControls({
	search,
	onChange,
	variant,
	earliestSelectable,
	filterSlot
}: {
	search: AnalyticsPeriodSearch
	/** Receives a patch of the shared search params to merge into the URL. */
	onChange: (patch: Partial<AnalyticsPeriodSearch>) => void
	variant: 'sales' | 'period' | 'navigation'
	/** Start (UTC) of the first sale; the pickers disable periods before it. Omit for no bound. */
	earliestSelectable?: Date
	filterSlot?: ReactNode
}) {
	const { L } = useI18n()
	const { interval, periodStart, comparePeriod, channels, compareChannels } = search
	const { data: salesChannelList, isLoading: salesChannelsLoading } = useListSalesChannels({
		enabled: variant === 'sales'
	})
	// The channel filter and comparison picker both group channels under their sales-channel group,
	// so we pass the raw list (each item carries its optional `group`) straight through.
	const salesChannels = salesChannelList ?? []

	// The "Compare by" selector picks what the overview compares against: another period, another
	// channel, or nothing. Seed it from the URL (a channel subject means channel mode, `none` means
	// off) and let the selector drive it thereafter.
	const [comparisonMode, setComparisonMode] = useState<ComparisonMode>(() => {
		if ((compareChannels?.length ?? 0) > 0) return 'channel'
		if (comparePeriod === 'none') return 'none'
		return 'period'
	})

	// Switching mode: period re-enables the period comparison (previous period) and drops any channel
	// subject; channel turns the period comparison off and waits for a subject; none turns it all off.
	const switchComparisonMode = (next: ComparisonMode) => {
		setComparisonMode(next)
		if (next === 'period') onChange({ comparePeriod: undefined, compareChannels: undefined })
		else if (next === 'channel') onChange({ comparePeriod: 'none' })
		else onChange({ comparePeriod: 'none', compareChannels: undefined })
	}

	// Changing the interval re-aligns period math, so drop the selected period and any specific
	// comparison-period date (it was tied to the old interval, so fall back to previous period). The
	// `none` (off) and same-period-last-year sentinels stay meaningful across intervals, so preserve
	// them — mirroring `changePeriod` below. In the sales view, any non-period mode still forces `none`.
	const changeInterval = (nextInterval: AnalyticsInterval) => {
		let nextCompare = comparePeriod
		if (comparePeriod && comparePeriod !== 'none' && comparePeriod !== COMPARE_LAST_YEAR) {
			nextCompare = undefined
		}
		if (variant === 'sales' && comparisonMode !== 'period') nextCompare = 'none'
		onChange({ interval: nextInterval, periodStart: undefined, comparePeriod: nextCompare })
	}

	// Paging (or jumping) keeps each comparison mode meaningful: previous period (omitted) and
	// same-period-last-year (the `lastYear` sentinel, re-resolved relative to the new period at query
	// time) pass through unchanged; a specific period stays fixed, but is dropped back to previous
	// once it would no longer sit before the selected period.
	const changePeriod = (ps: string | undefined) => {
		let nextCompare = comparePeriod
		if (comparePeriod && comparePeriod !== 'none' && comparePeriod !== COMPARE_LAST_YEAR) {
			const nextSelected = selectedPeriodStart(interval, ps)
			const compareStart = startOfPeriodUtc(interval, new Date(`${comparePeriod}T00:00:00.000Z`))
			if (compareStart.getTime() >= nextSelected.getTime()) nextCompare = undefined
		}
		onChange({ periodStart: ps, comparePeriod: nextCompare })
	}
	// The period jump picker (its popover handles interval + prev/next paging), stretched to the shared
	// control width so it lines up with the fields beside/under it.
	const periodPicker = (
		<PeriodJumpPicker
			interval={interval}
			periodStart={periodStart}
			earliestSelectable={earliestSelectable}
			onChange={changePeriod}
			onIntervalChange={changeInterval}
			className={CONTROL_WIDTH}
		/>
	)

	if (variant === 'period') {
		// Users view: period + comparison on one line on larger screens, wrapping to two equal-width
		// lines only when the container gets too narrow.
		return (
			<div className='flex w-full flex-wrap items-center gap-2 sm:w-auto'>
				{periodPicker}
				<CompareSelect
					interval={interval}
					periodStart={periodStart}
					comparePeriod={comparePeriod}
					earliestSelectable={earliestSelectable}
					onChange={cp => onChange({ comparePeriod: cp })}
					className={CONTROL_WIDTH}
				/>
			</div>
		)
	}

	if (variant === 'navigation') {
		return (
			<div className='flex w-full flex-wrap items-center gap-2 sm:w-auto'>
				{periodPicker}
				{filterSlot}
			</div>
		)
	}

	// Sales: period, channel filter, then the "Compare by …" dropdown and its matching control. The
	// fixed two-column width lays them out as an aligned 2×2 grid from `sm` up — period above "Compare
	// by", channel filter above the comparison value — with matching left and right edges.
	return (
		<div className='flex w-full flex-wrap items-center gap-2 sm:w-[27rem]'>
			{periodPicker}
			<SalesChannelMultiSelect
				salesChannels={salesChannels}
				value={channels ?? []}
				onChange={next => onChange({ channels: next.length > 0 ? next : undefined })}
				allLabel={L.Analytics.AllChannels()}
				isLoading={salesChannelsLoading}
				className={CONTROL_WIDTH}
			/>
			<ComparisonControls
				interval={interval}
				periodStart={periodStart}
				earliestSelectable={earliestSelectable}
				mode={comparisonMode}
				onModeChange={switchComparisonMode}
				compareSelectValue={comparePeriod}
				onCompareSelect={cp => onChange({ comparePeriod: cp })}
				channels={compareChannels ?? []}
				onChannelsChange={next => onChange({ compareChannels: next.length > 0 ? next : undefined })}
				salesChannels={salesChannels}
				channelsLoading={salesChannelsLoading}
			/>
		</div>
	)
}

/** Muted colour for the dashed comparison overlay on the revenue-over-time chart. */
const COMPARISON_SERIES_STROKE = 'var(--fg-muted)'

type RevenuePoint = {
	date: string
	revenue: { value: number; precision: number }
	orderCount: number
	partial: boolean
}

/**
 * Revenue-over-time area chart with the in-progress trailing bucket drawn dashed and an optional
 * dashed comparison overlay (aligned to `rows` by index). Shared by the Sales view and the revenue
 * report.
 */
export function RevenueOverTimeChart({
	isLoading,
	rows,
	comparisonRows,
	comparisonLabel,
	currencyCode
}: {
	isLoading: boolean
	rows: RevenuePoint[] | undefined
	/** Omit (or pass undefined) when comparison is off. */
	comparisonRows: RevenuePoint[] | undefined
	comparisonLabel: string
	currencyCode: string
}) {
	const { L } = useI18n()
	const revenueGradientId = `revgrad-${useId().replace(/:/g, '')}`

	const formatCurrency = (amount: { value: number; precision: number }) => L.Amount.Default({ amount, currencyCode })
	const formatCurrencyAxis = (value: number) => L.Amount.Default({ amount: { value, precision: 0 }, currencyCode })

	if (isLoading) return <Skeleton className='h-[300px]' />
	if (!rows || rows.length === 0) {
		return (
			<div className='text-muted-foreground flex h-[300px] items-center justify-center'>
				<Text>{L.Analytics.NoData()}</Text>
			</div>
		)
	}

	const revenueBase = rows.map((d, i) => {
		const comparisonPoint = comparisonRows?.[i]
		return {
			date: L.Date.ShortWithDate(parseISO(d.date).getTime()),
			value: d.revenue.value / Math.pow(10, d.revenue.precision),
			revenueAmount: d.revenue,
			orders: d.orderCount,
			partial: d.partial,
			comparisonValue: comparisonPoint
				? comparisonPoint.revenue.value / Math.pow(10, comparisonPoint.revenue.precision)
				: null,
			comparisonAmount: comparisonPoint?.revenue ?? null
		}
	})

	// Split into a solid series (completed buckets) and a dashed series (the in-progress trailing
	// bucket). The dashed series also carries the last completed point so the segment connects.
	const lastCompleteRevenueIdx = revenueBase.reduce((acc, d, i) => (d.partial ? acc : i), -1)
	const hasPartialRevenue = revenueBase.some(d => d.partial)
	const showComparisonSeries = (comparisonRows?.length ?? 0) > 0

	const chartData = revenueBase.map((d, i) => ({
		date: d.date,
		revenueAmount: d.revenueAmount,
		orders: d.orders,
		partial: d.partial,
		revenue: d.partial ? null : d.value,
		revenuePartial: hasPartialRevenue && i >= lastCompleteRevenueIdx ? d.value : null,
		comparison: showComparisonSeries ? d.comparisonValue : null,
		comparisonAmount: d.comparisonAmount
	}))

	const axisTickProps = { fontSize: 11 }

	return (
		<ChartContainer
			config={{ revenue: { label: L.Analytics.ChartLabels.Revenue(), color: CHART_SERIES_PRIMARY_STROKE } }}
			className='h-[300px]'
		>
			<AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
				<defs>
					<linearGradient id={revenueGradientId} x1='0' y1='0' x2='0' y2='1'>
						<stop offset='0%' stopColor={CHART_SERIES_PRIMARY_STROKE} stopOpacity={0.35} />
						<stop offset='100%' stopColor={CHART_SERIES_PRIMARY_STROKE} stopOpacity={0} />
					</linearGradient>
				</defs>
				<CartesianGrid stroke='var(--border-base)' strokeDasharray='3 3' />
				<XAxis dataKey='date' interval='preserveStartEnd' tick={axisTickProps} />
				<YAxis width={112} tickFormatter={formatCurrencyAxis} tick={axisTickProps} />
				<ChartTooltip
					content={<ChartTooltipContent hideLabel />}
					formatter={(value: any, _name: string, item: any) => {
						// The split leaves one half null per bucket; skip the empty half and avoid a
						// duplicate row where the solid and dashed segments meet.
						if (item?.value == null) return null
						if (item?.dataKey === 'revenuePartial' && item.payload?.revenue != null) return null
						if (item?.dataKey === 'comparison') {
							if (!item.payload?.comparisonAmount) return null
							return `${formatCurrency(item.payload.comparisonAmount)} · ${comparisonLabel}`
						}
						if (item?.payload?.revenueAmount) {
							const formatted = formatCurrency(item.payload.revenueAmount)
							return item.payload.partial ? `${formatted} ${L.Analytics.InProgress()}` : formatted
						}
						return value
					}}
				/>
				<Area
					type='monotone'
					dataKey='revenue'
					name={L.Analytics.ChartLabels.Revenue()}
					stroke={CHART_SERIES_PRIMARY_STROKE}
					strokeWidth={2}
					fillOpacity={1}
					fill={`url(#${revenueGradientId})`}
					activeDot={{ r: 4, strokeWidth: 0 }}
					connectNulls={false}
				/>
				<Area
					type='monotone'
					dataKey='revenuePartial'
					name={L.Analytics.ChartLabels.Revenue()}
					stroke={CHART_SERIES_PRIMARY_STROKE}
					strokeWidth={2}
					strokeDasharray='4 4'
					fillOpacity={0.15}
					fill={`url(#${revenueGradientId})`}
					activeDot={{ r: 4, strokeWidth: 0 }}
					connectNulls={false}
				/>
				{showComparisonSeries && (
					<Area
						type='monotone'
						dataKey='comparison'
						name={comparisonLabel}
						stroke={COMPARISON_SERIES_STROKE}
						strokeWidth={2}
						strokeDasharray='5 3'
						fill='transparent'
						activeDot={{ r: 3, strokeWidth: 0 }}
						connectNulls={false}
					/>
				)}
			</AreaChart>
		</ChartContainer>
	)
}

/**
 * Server-searchable product multi-select (debounced, paginated). An empty selection means "all
 * products". Used as the Basket view's anchor filter and the product report's product filter.
 */
export function ProductMultiSelect({
	value,
	onChange,
	placeholder,
	className
}: {
	value: string[]
	onChange: (uuids: string[]) => void
	placeholder: string
	/** Width/layout override so it can line up with the sibling toolbar controls. */
	className?: string
}) {
	const [search, setSearch] = useState('')
	const debouncedSearch = useDebounce(search)
	const productsQuery = usePaginatedListProducts({ limit: 50, sortOrder: 'asc', q: debouncedSearch || undefined })

	const options = useMemo(
		() =>
			(productsQuery.data?.pages.flatMap(page => page.products) ?? []).map(p => ({
				value: p.uuid,
				label: p.title
			})),
		[productsQuery.data]
	)

	return (
		<Combobox
			className={className ?? 'w-full sm:w-[16rem]'}
			value={value}
			onChange={next => onChange((next as string[]) ?? [])}
			options={options}
			optionsLoading={productsQuery.isLoading}
			onSearchValueChange={setSearch}
			fetchNextPage={() => productsQuery.fetchNextPage()}
			isFetchingNextPage={productsQuery.isFetchingNextPage}
			placeholder={placeholder}
		/>
	)
}
